import { useEffect, useRef } from 'react';
import useEventTracker from './useEventTracker';

/**
 * Custom hook to track tab_loaded events
 * Automatically tracks when a component/page is loaded
 * 
 * The event fires exactly once per component mount, regardless of context changes.
 * This prevents duplicate analytics events when pharmacy selection, location, or
 * other tracked context changes.
 * 
 * @param eventConfig - The tab loaded event configuration from TAB_LOADED_EVENTS
 * 
 * @example
 * ```tsx
 * import { useTrackTabLoaded } from 'app/hooks/useTrackTabLoaded';
 * import { TAB_LOADED_EVENTS } from 'app/tracking/constants';
 * 
 * function MyPage() {
 *   useTrackTabLoaded(TAB_LOADED_EVENTS.WHATS_NEW);
 *   // ... rest of component
 * }
 * ```
 */
export function useTrackTabLoaded(eventConfig: { action: string; type: string; tab: string }) {
  const trackEvent = useEventTracker();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    trackEvent(eventConfig.action, eventConfig);
  }, [trackEvent, eventConfig]);
}

